import {
  Actions,
  Button,
  Divider,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
} from 'braid-design-system';
import React from 'react';

import type { ListingRecord } from '#shared/types';
import {
  approveListing,
  declineListing,
  fetchAdminListings,
  unpublishListing,
  updateListing,
} from '#src/api/communityDirectory';
import { getAccessToken } from '#src/utils/cognitoAuth';

import { CreateListingDialog } from './CreateListingDialog';
import { ListingDetail } from './ListingDetail';
import {
  hasFieldUpdates,
  type ListingFieldUpdates,
} from './listingFieldUpdates';

type CommunityDirectoryAdminProps = {
  onError: (message: string | null) => void;
};

const ListingList = ({
  listings,
  emptyMessage,
  renderItem,
}: {
  listings: ListingRecord[];
  emptyMessage: string;
  renderItem: (listing: ListingRecord) => React.ReactNode;
}) => {
  if (listings.length === 0) {
    return <Text>{emptyMessage}</Text>;
  }

  return (
    <Stack space="medium">
      {listings.map((listing, index) => (
        <React.Fragment key={listing.id}>
          {index > 0 ? <Divider /> : null}
          {renderItem(listing)}
        </React.Fragment>
      ))}
    </Stack>
  );
};

export const CommunityDirectoryAdmin = ({
  onError,
}: CommunityDirectoryAdminProps) => {
  const [pending, setPending] = React.useState<ListingRecord[]>([]);
  const [approved, setApproved] = React.useState<ListingRecord[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);

  const loadListings = React.useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }

    const [pendingList, approvedList] = await Promise.all([
      fetchAdminListings(token, 'pending'),
      fetchAdminListings(token, 'approved'),
    ]);
    setPending(pendingList);
    setApproved(approvedList);
  }, []);

  React.useEffect(() => {
    loadListings()
      .catch(() => onError('Failed to load listings'))
      .finally(() => setLoading(false));
  }, [loadListings, onError]);

  const withToken = async (action: (token: string) => Promise<void>) => {
    const token = getAccessToken();
    if (!token) {
      const message = 'Session expired. Please log in again.';
      onError(message);
      throw new Error(message);
    }
    try {
      onError(null);
      await action(token);
      await loadListings();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Action failed';
      onError(message);
      throw err;
    }
  };

  const saveListingUpdates = async (
    token: string,
    id: string,
    updates: ListingFieldUpdates,
  ) => {
    if (hasFieldUpdates(updates)) {
      await updateListing(token, id, updates);
    }
  };

  if (loading) {
    return <Text>Loading listings…</Text>;
  }

  return (
    <Stack space="medium">
      <TabsProvider>
        <Tabs label="Listing queues">
          <Tab item="pending">Pending ({pending.length})</Tab>
          <Tab item="approved">Published ({approved.length})</Tab>
        </Tabs>
        <TabPanels>
          <TabPanel item="pending">
            <ListingList
              listings={pending}
              emptyMessage="No pending submissions."
              renderItem={(listing) => (
                <ListingDetail
                  listing={listing}
                  showModeration
                  onApprove={(updates) =>
                    withToken(async (token) => {
                      await saveListingUpdates(token, listing.id, updates);
                      await approveListing(token, listing.id);
                    })
                  }
                  onDecline={() =>
                    withToken((token) => declineListing(token, listing.id))
                  }
                  onSave={(updates) =>
                    withToken((token) =>
                      saveListingUpdates(token, listing.id, updates),
                    )
                  }
                />
              )}
            />
          </TabPanel>

          <TabPanel item="approved">
            <ListingList
              listings={approved}
              emptyMessage="No published listings."
              renderItem={(listing) => (
                <ListingDetail
                  listing={listing}
                  onUnpublish={() =>
                    withToken((token) => unpublishListing(token, listing.id))
                  }
                  onSave={(updates) =>
                    withToken((token) =>
                      saveListingUpdates(token, listing.id, updates),
                    )
                  }
                />
              )}
            />
          </TabPanel>
        </TabPanels>
      </TabsProvider>

      <Actions>
        <Button tone="formAccent" onClick={() => setCreateDialogOpen(true)}>
          Add listing
        </Button>
      </Actions>

      <CreateListingDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={loadListings}
        onError={onError}
      />
    </Stack>
  );
};
